import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Get redirect URL from query params or localStorage
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || localStorage.getItem('auth_redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`¡Bienvenido/a, ${user.name}!`);
      
      // Clear stored redirect
      localStorage.removeItem('auth_redirect');
      
      if (!user.onboarding_completed) {
        navigate('/onboarding');
      } else if (redirectUrl) {
        // Redirect to the original page they were trying to access
        navigate(redirectUrl);
      } else {
        navigate('/mi-camino');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-8 transition-colors"
            data-testid="login-back"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Volver al inicio
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center">
              <span className="text-white font-heading text-lg">A</span>
            </div>
            <span className="font-heading text-xl text-[hsl(var(--text-primary))]">
              Escuela Akáshica
            </span>
          </div>

          <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-2">
            Bienvenido/a de nuevo
          </h1>
          <p className="text-[hsl(var(--text-secondary))] mb-8">
            Introduce tus datos para acceder a tu cuenta
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-soft"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-soft pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-secondary))]"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-gold w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Accediendo...' : 'Acceder'}
            </Button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-[hsl(var(--accent))] hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>

          <p className="text-center text-[hsl(var(--text-secondary))] mt-8">
            ¿No tienes cuenta?{' '}
            <Link 
              to={redirectUrl ? `/register?redirect=${encodeURIComponent(redirectUrl)}` : '/register'} 
              className="text-[hsl(var(--accent))] hover:underline"
              data-testid="login-register-link"
            >
              Crear cuenta
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1685427387485-818ba609911f?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Naturaleza"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[hsl(var(--text-primary))]/30" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <blockquote className="text-white text-center">
            <p className="font-accent text-2xl italic mb-4">
              "Solo volver a casa dentro de ti."
            </p>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Login;
